/**
 * skins.module.css — ОФОРМЛЕНИЕ. `docs/design-direction.md` §5.1, §5.3, §5.4, §8.
 *
 * Two rules govern this file and neither is negotiable:
 *
 *  1. **The paint chip is painted from inline hexes, not from tokens.** Every colour a
 *     chip shows arrives on the element's `style` attribute from `lib/skin`'s registry,
 *     because a chip has to show a skin that is not the applied one. What lives here is
 *     the geometry: the plate, the rule, the ink bars, the touch target. Grep this file
 *     for a colour and you will find only the *chrome around* the chips.
 *  2. **Nothing here has a radius or a shadow.** §5.3 allows 0 and 999px; §5.5 licenses
 *     exactly one blurred shadow in the whole application and it belongs to the mini
 *     player. A picker is the single most tempting place in an app to reach for a
 *     rounded card with a lift on the selected one, and it is banned by construction.
 */

.root {
  display: flex;
  flex-direction: column;
  min-block-size: 0;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Masthead — the §8 screen contract, laid out exactly as `pads.module.css` lays it
   out, so the two destinations reached from the same nav strip read as siblings.
   ──────────────────────────────────────────────────────────────────────────────── */

.masthead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s3);
  padding-inline: var(--gutter);
  padding-block: var(--s2);
}

.mastheadLines {
  min-inline-size: 0;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The list.

   `gap: var(--s6)` and not `--s4`: each row is two stacked objects (a painted plate
   and a line of prose about it), so the space BETWEEN rows has to beat the space
   inside one or the blurb starts reading as a caption for the plate below it.
   ──────────────────────────────────────────────────────────────────────────────── */

.list {
  display: flex;
  flex-direction: column;
  gap: var(--s6);
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  display: block;
}

/*
 * The whole row is the target — §5.6's 44px floor is met several times over by the
 * plate alone, and a row where only the plate is tappable would leave the blurb
 * looking disabled.
 */
.pick {
  display: flex;
  flex-direction: column;
  gap: var(--s3);
  inline-size: 100%;
  min-block-size: var(--drop);
  padding: 0 0 0 var(--s3);
  border: 0;
  /* §5.4 `active`: the 3px left rule, the same mark the now-playing row wears. The
     unselected state keeps the same 3px so the text never shifts sideways on tap. */
  border-inline-start: 3px solid transparent;
  background: none;
  color: inherit;
  text-align: start;
  cursor: pointer;
}

.pick[data-active='true'] {
  border-inline-start-color: var(--border-active);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The paint chip.

   A printer's chip: a rectangle of the ground, hard-cut, ruled in the skin's own ink,
   carrying the print code and the name. Its `background`, `border-color` and `color`
   all arrive inline — see the file header.
   ──────────────────────────────────────────────────────────────────────────────── */

.chip {
  display: flex;
  flex-direction: column;
  gap: var(--s2);
  padding: var(--s3) var(--s4) var(--s4);
  border: 1px solid;
  border-radius: var(--r-cut);
}

.chipHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--s3);
}

.chipStamp {
  letter-spacing: 0.16em;
}

/*
 * `Выбрано` prints in the chip's OWN text ink, inherited from the plate, rather than
 * in `--accent`: on the applied skin those are the same value anyway, and on an
 * unapplied one the accent would be a colour that is not in this chip's world.
 *
 * It also un-does what `t-mono` does. §12.16 bans tracked uppercase Cyrillic in a
 * label tier — `.t-11` ships +0.16em and on Russian caps that reads as shouting.
 *
 * `:global(.t-mono)` is load-bearing and not decoration. `index.css` is imported after
 * every CSS module, so a bare `.chipMark` at (0,1,0) LOSES to `.t-mono` at (0,1,0) on
 * source order and the tracking survives — the trap recorded in
 * `wiki/pages/css-modules-lose-to-global-classes.md`. Pairing the two selectors takes
 * this rule to (0,2,0), which is the same recipe `components.module.css` and
 * `crateshelf.module.css` use for their own Russian labels.
 */
:global(.t-mono).chipMark {
  text-transform: none;
  letter-spacing: 0.02em;
  opacity: 0.72;
}

.chipName {
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

/* Three bars: the accent, the text, and the ground itself outlined so a near-black
   ground is still a visible swatch rather than a hole in the plate. */
.chipInks {
  display: flex;
  gap: var(--s1);
}

.chipInks i {
  display: block;
  inline-size: var(--drop);
  block-size: var(--s2);
  border: 1px solid transparent;
}

.blurb {
  display: block;
  color: var(--text-2);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The one thing the screen has to say in words.
   ──────────────────────────────────────────────────────────────────────────────── */

.note {
  margin-block-start: var(--s8);
  padding-block-start: var(--s4);
  border-block-start: 1px solid var(--border);
  color: var(--text-3);
}
